import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { statsType } from "../../interfaces/reports";
import { createAppAsyncThunk } from "../hooks";
import { baseEndpoint } from "../../api/endpoints";
import { getToken } from "../../api/getToken";

type Request = "getStats";
type RequestState = "pending" | "fulfilled" | "rejected";
interface reportStore {
  fileLink: string;
  stats: statsType;
  requests: {
    [requestName in Request]: {
      statuses: Record<string, RequestState | undefined>;
      errors: Record<string, string>;
    };
  };
}

export const getStats = createAsyncThunk<statsType, { locationID: string }>(
  "stats/getStats",
  async ({ locationID }, { rejectWithValue }) => {
    try {
      const token = await getToken();
      const response = await fetch(`${baseEndpoint}/statistics/${locationID}`, {
        headers: { authorization: token },
      });
      const data = await response.json();
      if (data.success) {
        return data.payload;
      } else {
        const errorMessage = data.title || "Failed to get floor data";
        return rejectWithValue(errorMessage);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState: reportStore = {
  fileLink: "",
  stats: null,
  requests: {
    getStats: {
      statuses: {},
      errors: {},
    },
  },
};
const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setFileLink: (state, action) => {
      state.fileLink = action.payload;
    },
    resetGetStatsStatus: (
      state,
      action: PayloadAction<{ locationID: string }>
    ) => {
      state.requests.getStats.statuses[action.payload.locationID] = undefined;
    },

    updateFloorStats: (state, action) => {
      const {
        DeskID,
        FloorID,
        LocationID,
        Status,
        Action,
        DeskCount,
        FloorName,
      } = action.payload;
      switch (Action) {
        case "increment": {
          const doesLoactionExist =
            state.stats && Object.keys(state.stats).includes(LocationID);
          if (!DeskID && doesLoactionExist) {
            let floorLocation = state.stats[LocationID] || [];
            floorLocation.push({
              FloorID,
              FloorName,
              Live: Status === "live",
              DeskStats: {
                Total: DeskCount,
                Occupied: 0,
                Unassigned: 0,
                UnderMaintenance: 0,
                Blocked: 0,
              },
            });
            
            state.stats = {
              ...state.stats,
              [LocationID]: floorLocation,
            };
            return;
          }

          break;
        }
        case "decrement": {
          if (!DeskID && state.stats?.[LocationID]) {
            const filtered = state.stats[LocationID].filter(
              (item) => item.FloorID !== FloorID
            );
            state.stats = { ...state.stats, [LocationID]: filtered };
          }

          break;
        }
        case "update": {
          if (!DeskID && state.stats?.[LocationID]) {
            const locationStats = state.stats?.[LocationID];
            if (locationStats) {
              state.stats = {
                ...state.stats,
                [LocationID]: locationStats.map((item) =>
                  item.FloorID === FloorID
                    ? { ...item, Live: Status === "live" }
                    : item
                ),
              };
            }
          }
          break;
        }
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(getStats.pending, (state, action) => {
      state.requests.getStats.statuses[action.meta.arg.locationID] = "pending";
    });
    builder.addCase(getStats.rejected, (state, action) => {
      state.requests.getStats.statuses[action.meta.arg.locationID] = "rejected";
      state.requests.getStats.errors[action.meta.arg.locationID] =
        action.error.message;
    });
    builder.addCase(getStats.fulfilled, (state, action) => {
      const data: statsType = action.payload;
      const locationID = action.meta.arg.locationID;

      const updated =
        data &&
        Object.values(data).reduce((acc, obj) => {
          if (!acc[locationID]) {
            acc[locationID] = {};
          }
          acc[locationID] = obj;
          return acc;
        }, {});

      state.stats = { ...state.stats, ...updated };
      state.requests.getStats.statuses[action.meta.arg.locationID] =
        "fulfilled";
    });
  },
});

export const { setFileLink, resetGetStatsStatus, updateFloorStats } =
  reportsSlice.actions;

export const fileLinkSelector = (state: RootState) => {
  return state.reports.fileLink;
};
export const statsSelector = (state: RootState) => {
  return state.reports.stats;
};
export const selectGetStatsStatus = (state: RootState, locationID: string) => {
  return state.reports.requests.getStats.statuses[locationID];
};

export default reportsSlice.reducer;
